import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNewScanWizard } from "../NewScanWizardProvider";
import { fetchToolCatalog, createScanJob } from "../../../api/scans";

const ReviewStep = () => {
  const navigate = useNavigate();
  const { selectedToolIds, targets, config } = useNewScanWizard();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadTools = async () => {
      try {
        setLoading(true);
        const data = await fetchToolCatalog(false);
        setTools(data);
      } catch (err) {
        setError("Failed to load scan tools");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadTools();
  }, []);

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);
      setError("");

      const payload = {
        targets,
        tools: selectedToolIds,
      };

      const response = await createScanJob(payload);
      const jobId = response.job?.id || response.jobs?.[0]?.id;

      if (jobId) {
        navigate(`/scans/${jobId}`);
      } else {
        setError("Failed to create scan job");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create scan job");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="step-content-loading">Loading review...</div>;
  }

  const selectedTools = tools.filter(t => selectedToolIds.includes(t.id));
  const toolsByCategory = selectedTools.reduce((acc, tool) => {
    const category = tool.category || "Other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(tool);
    return acc;
  }, {});

  return (
    <div className="review-step">
      <h2>Step 4: Review & Submit</h2>
      <p className="step-description">
        Review your scan configuration before submitting.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="review-sections">
        {/* Targets Summary */}
        <div className="review-section">
          <h3>📍 Targets</h3>
          <div className="review-content">
            <ul className="targets-list">
              {targets.map(target => (
                <li key={target}>{target}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Scan Tools by Category */}
        <div className="review-section">
          <h3>🔧 Scan Tools ({selectedToolIds.length})</h3>
          <div className="review-content">
            {Object.entries(toolsByCategory).map(([category, categoryTools]) => (
              <div key={category} className="category-group">
                <h4 className="category-name">{category}</h4>
                <ul className="tools-list">
                  {categoryTools.map(tool => (
                    <li key={tool.id} className="tool-item">
                      {tool.name}
                      {tool.active && <span className="active-indicator"> (Active)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Configuration Summary */}
        {config.nmap_profile && (
          <div className="review-section">
            <h3>⚙️ Configuration</h3>
            <div className="review-content">
              <dl>
                <dt>Nmap Profile:</dt>
                <dd>{config.nmap_profile}</dd>
              </dl>
            </div>
          </div>
        )}
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit Scan →"}
        </button>
      </div>
    </div>
  );
};

export default ReviewStep;
